import React from "react";
import {
  Body1,
} from "@fluentui/react-components";
import {
  ArrowTurnDownRightRegular,
} from "@fluentui/react-icons";
import { MPlanData, PlanDetailsProps } from "../../models";
import { getAgentIcon, getAgentDisplayNameWithSuffix } from '../../utils/agentIconUtils';
import ContentNotFound from "../NotFound/ContentNotFound";
import { safeAgentAccess, safeGet, typeSafeAccess } from '../../utils/safePropertyAccess';
import "../../styles/planpanelright.css";


const PlanPanelRight: React.FC<PlanDetailsProps> = ({
  planData,
  loading,
  planApprovalRequest,
  agentMessages = [],
  selectedTeam
}) => {

  if (!planData && !loading) {
    return <ContentNotFound subtitle="The requested page could not be found." />;
  }

  if (!planApprovalRequest) {
    return (
      <div className="plan-panel-right__no-data">
        No plan available
      </div>
    );
  }

  // Extract plan steps from the planApprovalRequest
  const extractPlanSteps = () => {
    if (!planApprovalRequest.steps || planApprovalRequest.steps.length === 0) {
      return [];
    }

    return planApprovalRequest.steps.map((step, index) => {
      const action = step.action || step.cleanAction || '';
      const isHeading = action.trim().endsWith(':');

      return {
        text: action.trim(),
        isHeading,
        key: `${index}-${action.substring(0, 20)}`
      };
    }).filter(step => step.text.length > 0);
  };

  // Render Plan Section
  const renderPlanSection = () => {
    const planSteps = extractPlanSteps();

    return (
      <div className="plan-section">
        <Body1 className="plan-section__title">
          Plan Overview
        </Body1>

        {planSteps.length === 0 ? (
          <div className="plan-section__empty">
            Plan is being generated...
          </div>
        ) : (
          <div className="plan-steps">
            {planSteps.map((step, index) => (
              <div key={step.key} className="plan-step">
                {step.isHeading ? (
                  // Heading - larger text, bold
                  <Body1 className="plan-step__heading">
                    {step.text}
                  </Body1>
                ) : (
                  // Sub-step - with arrow
                  <div className="plan-step__content">
                    <ArrowTurnDownRightRegular className="plan-step__arrow" />
                    <Body1 className="plan-step__text">
                      {step.text}
                    </Body1>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render Agents Section with Real-time Progress
  const renderAgentsSection = () => {
    // Get agents from planApprovalRequest.team (populated from plan steps)
    // Only fall back to selectedTeam if it matches the plan's team
    let agents: string[] = planApprovalRequest?.team || [];
    
    // Only use selectedTeam.agents if planApprovalRequest.team is empty
    // AND selectedTeam matches the plan's team_id
    if (agents.length === 0 && selectedTeam?.agents && planData?.plan?.team_id === selectedTeam?.team_id) {
      agents = selectedTeam.agents.map(a => a.name).filter(Boolean);
    }
    
    // Debug logging (can be removed in production)
    console.log('🔍 PlanPanelRight Debug:', {
      agents,
      planTeamId: planData?.plan?.team_id,
      selectedTeamId: selectedTeam?.team_id,
      selectedTeamAgents: selectedTeam?.agents?.map(a => a.name),
      agentMessagesCount: agentMessages.length,
      agentMessages: agentMessages.slice(-3),
      planApprovalRequest: planApprovalRequest ? 'exists' : 'null',
      planApprovalRequestTeam: planApprovalRequest?.team
    });
    
    console.log('🔍 PlanPanelRight Agent Messages Detail:', agentMessages.map(m => ({
      agent: m.agent,
      content: m.content?.substring(0, 50),
      timestamp: m.timestamp
    })));
    
    // Group agent messages by agent name to show latest status
    const agentStatusMap = new Map<string, { 
      latestMessage: string; 
      timestamp: number; 
      status: 'assigned' | 'working' | 'completed' 
    }>();
    
    // Initialize with assigned status for all agents
    // Use timestamp: 0 so any real message timestamp will be greater and will update the status
    agents.forEach(agentName => {
      agentStatusMap.set(agentName, {
        latestMessage: 'Assigned to plan',
        timestamp: 0,
        status: 'assigned'
      });
    });
    
    // Create a set of valid agent names for quick lookup
    const validAgentNames = new Set(agents);
    
    // Update with real-time messages - ONLY for agents that are in the plan's team
    agentMessages.forEach(message => {
      const agentName = safeAgentAccess.name(message);
      
      // Skip messages from agents not in the current team (stale messages from previous sessions)
      if (agents.length > 0 && !validAgentNames.has(agentName)) {
        return;
      }
      
      const content = safeAgentAccess.content(message);
      const timestamp = safeAgentAccess.timestamp(message);
      
      // Determine status from message content
      let status: 'assigned' | 'working' | 'completed' = 'working';
      const lowerContent = content.toLowerCase();
      if (lowerContent.includes('completed') || lowerContent.includes('finished')) {
        status = 'completed';
      } else if (lowerContent.includes('assigned')) {
        status = 'assigned';
      }
      
      // Update if this is a newer message
      const existing = agentStatusMap.get(agentName);
      if (!existing || timestamp > existing.timestamp) {
        agentStatusMap.set(agentName, {
          latestMessage: content.substring(0, 100) + (content.length > 100 ? '...' : ''),
          timestamp: timestamp,
          status
        });
      }
    });
    
    // Get all unique agents (from team + messages)
    // CRITICAL: Only show agents that are in the plan's team to avoid showing stale agents from previous sessions
    // Note: validAgentNames is already defined above
    
    // Only include message agents if they're in the plan's team
    // If agents array is empty, don't add any agents from messages (we don't know which team they belong to)
    const messageAgents = agents.length > 0 
      ? agentMessages.map(m => m.agent).filter(agentName => validAgentNames.has(agentName))
      : [];
    const allAgents = Array.from(new Set([...agents, ...messageAgents]));

    return (
      <div className="agents-section">
        <Body1 className="agents-section__title">
          Agent Team
        </Body1>

        {allAgents.length === 0 ? (
          <div className="agents-section__empty">
            No agents assigned yet...
          </div>
        ) : (
          <div className="agents-list">
            {allAgents.map((agentName, index) => {
              const agentStatus = agentStatusMap.get(agentName);
              const statusIcon = agentStatus?.status === 'completed' ? '✅' : 
                               agentStatus?.status === 'working' ? '⚡' : '📋';
              
              return (
                <div key={`${agentName}-${index}`} className="agent-item">
                  {/* Agent Icon */}
                  <div className="agent-item__icon">
                    {(() => {
                      try {
                        return getAgentIcon(agentName, planData, planApprovalRequest);
                      } catch (error) {
                        console.warn('Error getting agent icon for:', agentName, error);
                        return <div style={{ width: '32px', height: '32px', backgroundColor: '#ccc', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>👤</div>;
                      }
                    })()}
                  </div>

                  {/* Agent Info with Status */}
                  <div className="agent-item__info">
                    <Body1 className="agent-item__name">
                      {statusIcon} {(() => {
                        try {
                          return getAgentDisplayNameWithSuffix(agentName);
                        } catch (error) {
                          console.warn('Error getting agent display name for:', agentName, error);
                          return agentName || 'Unknown Agent';
                        }
                      })()}
                    </Body1>
                    {agentStatus && (
                      <div className="agent-item__status" style={{ 
                        fontSize: '12px', 
                        color: 'var(--colorNeutralForeground2)',
                        marginTop: '4px',
                        lineHeight: '1.3'
                      }}>
                        {agentStatus.latestMessage}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Main render
  return (
    <div className="plan-panel-right">
      {/* Plan section on top */}
      {renderPlanSection()}

      {/* Agents section below with line demarcation */}
      {renderAgentsSection()}
    </div>
  );
};

export default PlanPanelRight;